import { getOptionNameDefaults } from '@airship-platform/airship-proposal';
import { FlexBox } from '@airship-platform/jam-web/components';
import {
  CopyIcon,
  PlusIcon,
  ReorderIcon,
  TrashIcon,
} from '@airship-platform/jam-web/icons';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from '@hello-pangea/dnd';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { AppointmentProposalOption } from '../../../data-access/appointmentProposalOptions';
import DuplicateOptionModal from './DuplicateOptionModal';
import { OPTION_TEMPID_PREFIX, OptionFormInputs } from './optionForm.models';

const OptionNamesController: React.FC = () => {
  const [duplicateOptionId, setDuplicateOptionId] = useState<string>();

  const { control, watch } = useFormContext<OptionFormInputs>();

  const { fields, insert, remove, update } = useFieldArray({
    control,
    name: 'names',
  });

  const names = watch('names');

  const filteredNames = getOptionNameDefaults(names.map((n) => n.name)).filter(
    (name) =>
      !fields.some(
        (option) => option.name.toLowerCase() === name.toLowerCase()
      ) && !names.some((f) => f.name.toLowerCase() === name.toLowerCase())
  );

  const getNextSortOrder = () =>
    Math.max(0, ...fields.map((f) => f.sortOrder)) + 1;

  const getTempId = () =>
    [OPTION_TEMPID_PREFIX, Math.floor(Math.random() * 1000)].join('-');

  const onDuplicate = (option: AppointmentProposalOption) => {
    insert(
      fields.length,
      {
        name: option.name,
        optionId: option.appointmentProposalOptionId,
        templateOptionIds: [],
        sortOrder: getNextSortOrder(),
      },
      { shouldFocus: true }
    );
    setDuplicateOptionId(undefined);
  };

  const handleDragEnd = (
    result: DropResult,
    items: OptionFormInputs['names']
  ) => {
    if (
      !result.destination ||
      result.source.index === result.destination.index
    ) {
      return;
    }

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    const itemsCopy = Array.from(items);
    const [removed] = itemsCopy.splice(sourceIndex, 1);
    itemsCopy.splice(destinationIndex, 0, removed);
    itemsCopy.forEach((item, index) => {
      update(index, {
        ...item,
        sortOrder: index + 1,
      });
    });
  };

  const buildAdornments = (option: (typeof fields)[number], index: number) => (
    <InputAdornment position="end">
      <IconButton
        edge={option.optionId.startsWith(OPTION_TEMPID_PREFIX) && 'end'}
        disabled={fields.length <= 1}
        onClick={() => remove(index)}
      >
        <TrashIcon />
      </IconButton>
      {!option.optionId.startsWith(OPTION_TEMPID_PREFIX) && (
        <IconButton
          edge="end"
          onClick={() => setDuplicateOptionId(option.optionId)}
        >
          <CopyIcon />
        </IconButton>
      )}
    </InputAdornment>
  );

  return (
    <FlexBox
      flexDirection={'column'}
      justifyContent={'center'}
      height={'100%'}
      width={'100%'}
      gap={3}
    >
      <FlexBox gap={3} flexDirection={'column'}>
        <DragDropContext onDragEnd={(result) => handleDragEnd(result, names)}>
          <Droppable droppableId="optionNamesDroppable">
            {(provided) => (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
              >
                {fields.map((option, index) => (
                  <Draggable
                    key={option.optionId}
                    draggableId={option.optionId}
                    index={index}
                  >
                    {(provided) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        sx={{ display: 'flex', gap: 1 }}
                      >
                        <Controller
                          key={option.id}
                          name={`names.${index}.name`}
                          control={control}
                          render={({ field, fieldState, formState }) => (
                            <TextField
                              type="text"
                              label={`Option ${index + 1}`}
                              {...field}
                              error={
                                !!Object.keys(
                                  formState.errors.names?.[index] || {}
                                ).length
                              }
                              helperText={
                                fieldState.error?.message ||
                                formState.errors.names?.[index]?.optionId
                                  ?.message
                              }
                              slotProps={{
                                input: {
                                  endAdornment: buildAdornments(option, index),
                                },
                              }}
                            />
                          )}
                        />
                        <IconButton
                          {...provided.dragHandleProps}
                          size="small"
                          sx={{ cursor: 'grab' }}
                        >
                          <ReorderIcon />
                        </IconButton>
                      </Box>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>

        <FlexBox gap={1} flexWrap={'wrap'}>
          {filteredNames.map((suggestedName) => (
            <Button
              key={suggestedName}
              variant="contained"
              color="softGold"
              onClick={() => {
                const existingNameIndex =
                  names.some((d) => d.name === '') &&
                  names.findIndex((d) => d.name === '');

                return typeof existingNameIndex === 'number'
                  ? update(existingNameIndex, {
                      name: suggestedName,
                      optionId: getTempId(),
                      templateOptionIds: [],
                      sortOrder: existingNameIndex + 1,
                    })
                  : insert(
                      fields.length,
                      {
                        name: suggestedName,
                        optionId: getTempId(),
                        templateOptionIds: [],
                        sortOrder: getNextSortOrder(),
                      },
                      { shouldFocus: true }
                    );
              }}
            >
              {suggestedName}
            </Button>
          ))}

          <Button
            variant="contained"
            color="softGold"
            onClick={() =>
              insert(
                fields.length,
                {
                  name: '',
                  optionId: getTempId(),
                  templateOptionIds: [],
                  sortOrder: getNextSortOrder(),
                },
                { shouldFocus: true }
              )
            }
            startIcon={<PlusIcon />}
          >
            Add option
          </Button>
        </FlexBox>
      </FlexBox>

      {duplicateOptionId && (
        <DuplicateOptionModal
          isOpen={!!duplicateOptionId}
          optionId={duplicateOptionId}
          onClose={() => setDuplicateOptionId(undefined)}
          onDuplicated={onDuplicate}
        />
      )}
    </FlexBox>
  );
};

export default OptionNamesController;
